import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Save, ArrowRight } from "lucide-react";

const COLUMNS = [
  { key: "idea",      label: "Fikir",         color: "border-cc-faint" },
  { key: "writing",   label: "Yazılıyor",     color: "border-amber-400" },
  { key: "review",    label: "Revizyon",      color: "border-blue-400" },
  { key: "published", label: "Yayında",       color: "border-emerald-500" },
  { key: "refresh",   label: "Güncellenecek", color: "border-purple-400" },
];

const PRI_COLOR = { high: "text-red-600", medium: "text-cc-sepia", low: "text-cc-muted" };
const PRI_LABEL = { high: "Yüksek", medium: "Orta", low: "Düşük" };

export default function AdminIdeasPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/content-ideas"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const byStatus = (status) => items.filter((i) => i.status === status);

  const startNew = () => setEditing({ title: "", description: "", target_keywords: [], keyword_input: "",
                                       target_type: "journal", status: "idea", priority: "medium" });

  const save = async () => {
    try {
      const payload = { ...editing };
      delete payload.keyword_input;
      delete payload.id;
      if (editing.id) await api.put(`/admin/content-ideas/${editing.id}`, payload);
      else await api.post("/admin/content-ideas", payload);
      setEditing(null); load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const moveStatus = async (id, status) => {
    await api.patch(`/admin/content-ideas/${id}/status`, { status });
    load();
  };

  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/admin/content-ideas/${id}`); load(); };

  const addKeyword = () => {
    const k = editing.keyword_input?.trim();
    if (!k) return;
    setEditing({ ...editing, target_keywords: [...(editing.target_keywords || []), k], keyword_input: "" });
  };

  return (
    <div data-testid="admin-ideas">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-serif text-4xl text-cc-text mb-2">İçerik Görev Sistemi</h1>
        <p className="text-sm text-cc-muted">{items.length} içerik fikri · 24 ay boyunca otorite inşa eden disiplin</p></div>
        <button onClick={startNew} className="cc-btn" data-testid="new-idea-btn"><Plus size={14} /> Yeni Fikir</button>
      </div>

      {editing && (
        <div className="bg-white border border-admin-border p-6 mb-8" data-testid="idea-editor">
          <label className="text-xs text-cc-muted block mb-4">Başlık
            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="cc-input" placeholder="Örn: Ihlara Vadisi yürüyüş rehberi" /></label>
          <label className="text-xs text-cc-muted block mb-4">Açıklama (opsiyonel)
            <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="cc-input min-h-[60px]" /></label>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <label className="text-xs text-cc-muted">Tür
              <select value={editing.target_type} onChange={(e) => setEditing({ ...editing, target_type: e.target.value })} className="cc-input">
                <option value="journal">Günlük</option>
                <option value="guide">Rehber</option>
                <option value="encyclopedia">Ansiklopedi</option>
              </select>
            </label>
            <label className="text-xs text-cc-muted">Öncelik
              <select value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} className="cc-input">
                <option value="high">Yüksek</option><option value="medium">Orta</option><option value="low">Düşük</option>
              </select>
            </label>
            <label className="text-xs text-cc-muted">Durum
              <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="cc-input">
                {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
          </div>
          <div className="mb-4">
            <label className="text-xs text-cc-muted">Anahtar Kelimeler</label>
            <div className="flex gap-2 mt-1">
              <input value={editing.keyword_input || ""} onChange={(e) => setEditing({ ...editing, keyword_input: e.target.value })}
                     onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                     className="cc-input flex-1" placeholder="anahtar kelime + Enter" />
              <button onClick={addKeyword} className="cc-btn cc-btn-ghost text-[10px] py-2">Ekle</button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(editing.target_keywords || []).map((k, i) => (
                <span key={i} className="px-2 py-1 text-[10px] bg-cc-bg border border-admin-border tracking-wider">
                  {k} <button onClick={() => setEditing({ ...editing, target_keywords: editing.target_keywords.filter((_, j) => j !== i) })} className="ml-1 text-red-500">×</button>
                </span>
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={save} className="cc-btn text-[10px] py-2" data-testid="idea-save"><Save size={14} /> Kaydet</button>
            <button onClick={() => setEditing(null)} className="cc-btn cc-btn-ghost text-[10px] py-2">İptal</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kanban">
        {COLUMNS.map((col) => (
          <div key={col.key} className={`bg-white border-t-2 ${col.color} border border-admin-border p-3`} data-testid={`column-${col.key}`}>
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-admin-border">
              <h3 className="font-medium text-cc-text text-sm">{col.label}</h3>
              <span className="text-[10px] text-cc-muted">{byStatus(col.key).length}</span>
            </div>
            <div className="space-y-2 min-h-[100px]">
              {byStatus(col.key).map((i) => (
                <div key={i.id} className="bg-admin-bg/60 border border-admin-border p-3 group" data-testid={`idea-${i.id}`}>
                  <div className={`text-[9px] tracking-wider uppercase ${PRI_COLOR[i.priority]} mb-1`}>{PRI_LABEL[i.priority]} · {i.target_type === "journal" ? "Günlük" : i.target_type === "guide" ? "Rehber" : "Ansiklopedi"}</div>
                  <div className="text-xs text-cc-text leading-snug mb-2">{i.title}</div>
                  {i.target_keywords?.length > 0 && (
                    <div className="text-[9px] text-cc-muted mb-2 line-clamp-1">{i.target_keywords.join(", ")}</div>
                  )}
                  <div className="flex items-center justify-between opacity-50 group-hover:opacity-100 transition">
                    <button onClick={() => setEditing(i)} className="text-[10px] text-cc-sepia">Düzenle</button>
                    <div className="flex items-center gap-1">
                      {COLUMNS.filter((c) => c.key !== i.status).slice(0, 2).map((next) => (
                        <button key={next.key} onClick={() => moveStatus(i.id, next.key)} className="text-[10px] text-cc-muted hover:text-cc-sepia px-1.5 py-0.5" title={`→ ${next.label}`}>
                          {next.label.slice(0, 4)}<ArrowRight size={8} className="inline" />
                        </button>
                      ))}
                      <button onClick={() => del(i.id)} className="text-red-500"><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              ))}
              {byStatus(col.key).length === 0 && <p className="text-[10px] text-cc-faint italic py-4 text-center">Bu kolonda fikir yok.</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
